import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import LogoSVG from '../components/LogoSVG';

const stripExt = (n) => n ? n.replace(/\.(mp4|mov|avi|mkv|wmv|webm)$/i, '') : n;

const STATUS_MAP = {
  none:        { label: '상태없음', color: '#666666' },
  pending:     { label: '보류',     color: '#ff4d4d' },
  in_progress: { label: '진행중',   color: '#4d9fff' },
  done:        { label: '완료',     color: '#28c76f' },
};

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <path d="M21.1,5.2h-4.6v-2.3c0-1.3-1-2.3-2.3-2.3h-4.6c-1.3,0-2.3,1-2.3,2.3v2.3H2.9c-.6,0-1.1.5-1.1,1.1s.5,1.1,1.1,1.1h0l1.1,12.6c0,1.9,1.5,3.4,3.4,3.4h9.1c1.9,0,3.4-1.5,3.4-3.3l1.1-12.7h0c.6,0,1.1-.5,1.1-1.1s-.5-1.1-1.1-1.1ZM9.7,2.9h4.6v2.3h-4.6v-2.3ZM17.7,20c0,.6-.5,1.1-1.1,1.1H7.4c-.6,0-1.1-.5-1.1-1.2l-1-12.5h13.5l-1,12.6Z"/>
    <path d="M9.7,18.8c.6,0,1.1-.5,1.1-1.1v-6.8c0-.6-.5-1.1-1.1-1.1s-1.1.5-1.1,1.1v6.8c0,.6.5,1.1,1.1,1.1Z"/>
    <path d="M14.3,18.8c.6,0,1.1-.5,1.1-1.1v-6.8c0-.6-.5-1.1-1.1-1.1s-1.1.5-1.1,1.1v6.8c0,.6.5,1.1,1.1,1.1Z"/>
  </svg>
);

const Arrow = () => (
  <svg width="7" height="12" viewBox="0 0 7 12" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="1 1 6 6 1 11"/>
  </svg>
);

function formatDuration(seconds) {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function VideoList() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const location = useLocation();
  const state = location.state || {};
  const [videos, setVideos] = useState([]);
  const [projectName, setProjectName] = useState(state.projectName || '');
  const [managerName, setManagerName] = useState(state.managerName || '');
  const [managerId, setManagerId] = useState(state.managerId || null);
  const [sliderValue, setSliderValue] = useState(3);
  const [viewMode, setViewMode] = useState('card');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);
  const [addHovered, setAddHovered] = useState(false);
  const [selected, setSelected] = useState({});
  const [deleteSelectedConfirm, setDeleteSelectedConfirm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const [sharePanel, setSharePanel] = useState(false);
  const [sharePanelVisible, setSharePanelVisible] = useState(false);
  const [sharePw, setSharePw] = useState('');
  const [sharePw2, setSharePw2] = useState('');
  const [shareErr, setShareErr] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [shareStep, setShareStep] = useState(1);
  const [shareCopied, setShareCopied] = useState(false);
  const editInputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchAll(); }, [projectId]);

  // 목록 자동 갱신 (3초)
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const res = await axios.get(`/api/videolist/index.php?project_id=${projectId}`, { withCredentials: true });
        setVideos(res.data);
      } catch {}
    }, 3000);
    return () => clearInterval(t);
  }, [projectId]);

  const fetchAll = async () => {
    try {
      const res = await axios.get(`/api/videolist/index.php?project_id=${projectId}`, { withCredentials: true });
      setVideos(res.data);
      if (res.data[0]) {
        if (res.data[0].project_name) setProjectName(res.data[0].project_name);
        if (res.data[0].manager_name) setManagerName(res.data[0].manager_name);
        if (res.data[0].manager_id) setManagerId(res.data[0].manager_id);
      }
    } catch { navigate('/login'); }
  };

  const openSharePanel = () => {
    setShareStep(1); setSharePw(''); setSharePw2(''); setShareErr(''); setShareUrl('');
    setSharePanel(true); setTimeout(() => setSharePanelVisible(true), 10);
  };

  const closeSharePanel = () => {
    setSharePanelVisible(false); setTimeout(() => setSharePanel(false), 260);
  };

  const handleCreateShare = async () => {
    if (sharePw && sharePw !== sharePw2) { setShareErr('비밀번호가 일치하지 않습니다'); return; }
    try {
      const res = await axios.post('/api/sharelinks/index.php', { type: 'folder', target_id: Number(projectId), password: sharePw }, { withCredentials: true });
      setShareUrl(res.data.url); setShareStep(2);
    } catch { setShareErr('링크 생성에 실패했습니다'); }
  };

  const openUpload = () => {
    setUploadOpen(true);
    setTimeout(() => setPanelVisible(true), 10);
  };

  const closeUpload = () => {
    setPanelVisible(false);
    setTimeout(() => { setUploadOpen(false); setUploadFiles([]); setAllDone(false); }, 260);
  };

  const handleRename = async (id) => {
    if (!editName.trim()) { setEditId(null); return; }
    await axios.put('/api/videolist/index.php', { id, name: editName }, { withCredentials: true });
    setEditId(null); fetchAll();
  };

  const handleDelete = async (id) => {
    await axios.delete('/api/videolist/index.php', { data: { id }, withCredentials: true });
    setDeleteConfirm(null); fetchAll();
  };

  const toggleSelect = (v) => {
    const key = String(v.id);
    setSelected(p => {
      const next = { ...p };
      if (next[key]) delete next[key]; else next[key] = v;
      return next;
    });
  };

  const selectedCount = Object.keys(selected).length;

  const handleDeleteSelected = async () => {
    for (const v of Object.values(selected)) {
      await axios.delete('/api/videolist/index.php', { data: { id: v.id }, withCredentials: true });
    }
    setSelected({}); setDeleteSelectedConfirm(false); fetchAll();
  };

  const ALLOWED_EXT = ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'webm'];
  const MAX_SIZE = 4 * 1024 * 1024 * 1024; // 4GB

  const addFiles = (files) => {
    const list = Array.from(files);
    const invalid = list.filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      return !ALLOWED_EXT.includes(ext) || f.size > MAX_SIZE;
    });
    const valid = list.filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      return ALLOWED_EXT.includes(ext) && f.size <= MAX_SIZE;
    });
    if (invalid.length > 0) {
      const names = invalid.map(f => {
        const ext = f.name.split('.').pop().toLowerCase();
        if (!ALLOWED_EXT.includes(ext)) return `${f.name} (지원하지 않는 형식)`;
        return `${f.name} (4GB 초과)`;
      }).join('\n');
      alert(`다음 파일은 추가할 수 없습니다:\n${names}`);
    }
    if (valid.length === 0) return;
    setUploadFiles(prev => {
      const merged = [...prev];
      valid.forEach(f => {
        if (!merged.find(x => x.file.name === f.name && x.file.size === f.size))
          merged.push({ file: f, progress: 0, status: 'pending' });
      });
      return merged;
    });
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0 || uploading) return;
    setUploading(true);
    for (let i = 0; i < uploadFiles.length; i++) {
      const item = uploadFiles[i];
      if (item.status === 'done') continue;
      const name = item.file.name.replace(/\.[^.]+$/, '');
      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('name', name);
      formData.append('file', item.file);
      try {
        await axios.post('/api/upload/index.php', formData, {
          withCredentials: true,
          onUploadProgress: e => {
            if (e.total) {
              const pct = Math.round(e.loaded / e.total * 100);
              setUploadFiles(prev => prev.map((x, j) => j === i ? { ...x, progress: pct } : x));
            }
          },
        });
        setUploadFiles(prev => prev.map((x, j) => j === i ? { ...x, progress: 100, status: 'done' } : x));
      } catch {
        setUploadFiles(prev => prev.map((x, j) => j === i ? { ...x, status: 'error' } : x));
      }
    }
    setUploading(false);
    setAllDone(true);
    fetchAll();
  };

  const cardWidth = 160 + sliderValue * 24;

  return (
    <div style={s.wrap}>
      {/* 상단바 */}
      <div style={s.topbar}>
        <div style={s.topbarLeft}>
          <LogoSVG size={32} />
          <div style={s.divider} />
          <div style={s.topbarPath}>
            <span style={s.topbarDim} onClick={() => navigate('/')}>WORKSPACE</span>
            <Arrow />
            <span style={{ ...s.topbarDim, display: 'inline-flex', alignItems: 'center', gap: 5 }} onClick={() => managerId && navigate(`/projects/${managerId}`)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
              {managerName || '…'}
            </span>
            <Arrow />
            <span style={{ ...s.topbarCurrent, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
              {projectName || '…'}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {selectedCount > 0 && (
            <button style={s.deleteSelectedBtn} onClick={() => setDeleteSelectedConfirm(true)}>선택 삭제 ({selectedCount})</button>
          )}
          <button style={s.shareBtn} onClick={openSharePanel}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
            공유
          </button>
        </div>
      </div>

      {/* 컨트롤 */}
      <div style={s.controlRow}>
        <span style={s.sectionLabel}>영상 {videos.length}개</span>
        <div style={s.sliderGroup}>
          {viewMode === 'card' && (
            <>
              <button style={s.sliderBtn} onClick={() => setSliderValue(v => Math.max(1, v - 1))}>
                <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </button>
              <input type="range" min={1} max={5} value={sliderValue}
                onChange={e => setSliderValue(Number(e.target.value))} style={s.slider} />
              <button style={s.sliderBtn} onClick={() => setSliderValue(v => Math.min(5, v + 1))}>
                <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor"><path d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6V5z"/></svg>
              </button>
              <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.15)', margin: '0 4px' }} />
            </>
          )}
          <button style={{ ...s.sliderBtn, color: viewMode === 'card' ? '#a5a3ff' : 'rgba(255,255,255,0.4)' }} onClick={() => setViewMode('card')} title="카드 보기">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
          </button>
          <button style={{ ...s.sliderBtn, color: viewMode === 'list' ? '#a5a3ff' : 'rgba(255,255,255,0.4)' }} onClick={() => setViewMode('list')} title="리스트 보기">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
          </button>
        </div>
      </div>

      {/* 그리드 */}
      {viewMode === 'card' ? (
      <div style={s.grid}>
        {videos.map(v => {
          const st = STATUS_MAP[v.status] || STATUS_MAP.none;
          return (
            <div key={v.id} style={{ ...s.videoCard, width: cardWidth, borderColor: selected[v.id] ? '#5755df' : hoveredId === v.id ? '#5755df' : 'rgba(255,255,255,0.12)', background: hoveredId === v.id ? 'rgba(87,85,223,0.07)' : 'rgba(255,255,255,0.03)', transform: hoveredId === v.id ? 'translateY(-2px)' : 'none', transition: 'border-color .18s, background .18s, transform .18s', position: 'relative' }}
              onMouseEnter={() => setHoveredId(v.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => navigate(`/viewer/${v.id}`, { state: { projectName, managerName, managerId, projectId: Number(projectId), videoName: v.name } })}>
              <div
                onClick={e => { e.stopPropagation(); toggleSelect(v); }}
                style={{ position: 'absolute', top: 8, left: 8, width: 16, height: 16, border: '1.5px solid rgba(255,255,255,0.6)', borderRadius: 3, background: 'rgba(0,0,0,0.2)', cursor: 'pointer', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {selected[String(v.id)] && <svg width="10" height="10" viewBox="0 0 12 12"><polyline points="1,6 5,10 11,2" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
              <div style={s.thumbnail}>
                {v.thumbnail_path
                  ? <img src={v.thumbnail_path} alt="" style={s.thumbImg} />
                  : <div style={s.thumbPlaceholder}>
                      <div style={s.playIcon}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                      </div>
                      <span style={s.thumbLabel}>썸네일 없음</span>
                    </div>}
                                <button style={s.deleteBtn} onClick={e => { e.stopPropagation(); setDeleteConfirm(v); }}>
                  <TrashIcon />
                </button>
              </div>
              <div style={s.cardBody}>
                <div style={s.cardName}>
                  {editId === v.id ? (
                    <input ref={editInputRef} style={s.nameInput} value={editName}
                      onChange={e => setEditName(e.target.value)}
                      onBlur={() => handleRename(v.id)}
                      onKeyDown={e => { if (e.key === 'Enter') handleRename(v.id); if (e.key === 'Escape') setEditId(null); }}
                      onClick={e => e.stopPropagation()} />
                  ) : (
                    <>
                      <span style={s.cardNameSpan}>{stripExt(v.name).length > 12 ? stripExt(v.name).slice(0, 12) + '...' : stripExt(v.name)}</span>
                      <button style={s.pencilBtn}
                        onClick={e => { e.stopPropagation(); setEditId(v.id); setEditName(v.name); setTimeout(() => editInputRef.current?.focus(), 50); }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                      </button>
                    </>
                  )}
                </div>
                <div style={s.cardMeta}>
                  <span style={s.cardInfo}>버전 {v.version_count || 1}개</span>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    {Number(v.has_new_feedback) > 0 && <span style={s.badgeNew}>새 피드백</span>}
                    <span style={s.statusPill}>
                      <span style={{ ...s.statusDot, background: st.color }} />
                      {st.label}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* + 추가 카드 */}
        <div style={{ ...s.addCard, width: cardWidth, aspectRatio: '1/1', background: addHovered ? 'rgba(87,85,223,0.07)' : 'transparent', color: addHovered ? '#a5a3ff' : 'rgba(255,255,255,0.45)', transform: addHovered ? 'translateY(-2px)' : 'none', transition: 'background .18s, color .18s, transform .18s', position: 'relative', border: 'none' }}
          onMouseEnter={() => setAddHovered(true)}
          onMouseLeave={() => setAddHovered(false)}
          onClick={openUpload}>
          <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }} xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="calc(100% - 2px)" height="calc(100% - 2px)" rx="15" ry="15"
              fill="none"
              stroke={addHovered ? '#5755df' : 'rgba(255,255,255,0.2)'}
              strokeWidth="1.4"
              strokeDasharray="6.8 6.8"
              style={{ transition: 'stroke .18s' }}
            />
          </svg>
          <svg width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.0" strokeLinecap="round">
            <path d="M12 5 L12 19 M5 12 L19 12"/>
          </svg>
        </div>
      </div>
      ) : (
      <div style={s.listWrap}>
        {videos.map(v => {
          const st = STATUS_MAP[v.status] || STATUS_MAP.none;
          return (
            <div key={v.id}
              style={{ ...s.listRow, borderColor: selected[v.id] ? '#5755df' : hoveredId === v.id ? '#5755df' : 'rgba(255,255,255,0.12)', background: hoveredId === v.id ? 'rgba(87,85,223,0.07)' : 'rgba(255,255,255,0.03)', transition: 'border-color .18s, background .18s' }}
              onMouseEnter={() => setHoveredId(v.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => navigate(`/viewer/${v.id}`, { state: { projectName, managerName, managerId, projectId: Number(projectId), videoName: v.name } })}>
              <div
                onClick={e => { e.stopPropagation(); toggleSelect(v); }}
                style={{ width: 16, height: 16, border: '1.5px solid rgba(255,255,255,0.6)', borderRadius: 3, background: 'rgba(0,0,0,0.2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {selected[String(v.id)] && <svg width="10" height="10" viewBox="0 0 12 12"><polyline points="1,6 5,10 11,2" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
              <div style={s.listThumb}>
                {v.thumbnail_path
                  ? <img src={v.thumbnail_path} alt="" style={s.thumbImg} />
                  : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', color: 'rgba(255,255,255,0.2)' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                    </div>}
              </div>
              <div style={s.listName}>
                {editId === v.id ? (
                  <input ref={editInputRef} style={s.nameInput} value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onBlur={() => handleRename(v.id)}
                    onKeyDown={e => { if (e.key === 'Enter') handleRename(v.id); if (e.key === 'Escape') setEditId(null); }}
                    onClick={e => e.stopPropagation()} />
                ) : (
                  <>
                    <span style={s.cardNameSpan}>{stripExt(v.name).length > 30 ? stripExt(v.name).slice(0, 30) + '...' : stripExt(v.name)}</span>
                    <button style={s.pencilBtn}
                      onClick={e => { e.stopPropagation(); setEditId(v.id); setEditName(v.name); setTimeout(() => editInputRef.current?.focus(), 50); }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                    </button>
                  </>
                )}
              </div>
              <span style={{ ...s.cardInfo, flexShrink: 0 }}>버전 {v.version_count || 1}개</span>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0, width: 130, justifyContent: 'flex-end' }}>
                {Number(v.has_new_feedback) > 0 && <span style={s.badgeNew}>새 피드백</span>}
                <span style={s.statusPill}>
                  <span style={{ ...s.statusDot, background: st.color }} />
                  {st.label}
                </span>
              </div>
              <button style={{ ...s.deleteBtn, position: 'static', opacity: hoveredId === v.id ? 1 : 0, flexShrink: 0 }} onClick={e => { e.stopPropagation(); setDeleteConfirm(v); }}>
                <TrashIcon />
              </button>
            </div>
          );
        })}
        <div style={{ ...s.listRow, border: '1.4px dashed rgba(255,255,255,0.2)', justifyContent: 'center', color: addHovered ? '#a5a3ff' : 'rgba(255,255,255,0.45)', background: addHovered ? 'rgba(87,85,223,0.07)' : 'transparent', transition: 'background .18s, color .18s' }}
          onMouseEnter={() => setAddHovered(true)}
          onMouseLeave={() => setAddHovered(false)}
          onClick={openUpload}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M12 5 L12 19 M5 12 L19 12"/></svg>
        </div>
      </div>
      )}

      {/* 공유 패널 */}
      {sharePanel && (
        <div style={{ ...s.panelOverlay, pointerEvents: sharePanelVisible ? 'all' : 'none' }} onClick={closeSharePanel}>
          <div style={{ ...s.uploadPanel, transform: sharePanelVisible ? 'translateX(0)' : 'translateX(100%)' }} onClick={e => e.stopPropagation()}>
            <div style={s.uploadHeader}>
              <span style={s.uploadTitle}>공유 링크 생성</span>
              <button style={s.uploadClose} onClick={closeSharePanel}>
                <svg width="27" height="27" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {shareStep === 1 ? (<>
                <div style={s.shareField}><label style={s.shareLabel}>비밀번호</label><input style={s.shareInput} type="password" placeholder="공유 비밀번호" value={sharePw} onChange={e => setSharePw(e.target.value)} /></div>
                <div style={s.shareField}><label style={s.shareLabel}>비밀번호 확인</label><input style={s.shareInput} type="password" placeholder="비밀번호 재입력" value={sharePw2} onChange={e => setSharePw2(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreateShare()} /></div>
                {shareErr && <div style={{ color: '#ff6b6b', fontSize: 10 }}>{shareErr}</div>}
                <div style={s.shareActions}>
                  <button style={s.shareActionBtn} onClick={closeSharePanel}>취소</button>
                  <button style={{ ...s.shareActionBtn, background: '#5755df', color: '#fff', borderColor: '#5755df' }} onClick={handleCreateShare}>링크 생성</button>
                </div>
              </>) : (<>
                <div style={s.shareField}><label style={s.shareLabel}>생성된 링크</label><div style={s.shareUrlBox}>{shareUrl}</div></div>
                <div style={s.shareActions}>
                  <button style={s.shareActionBtn} onClick={closeSharePanel}>닫기</button>
                  <button
                    style={{ ...s.shareActionBtn, background: shareCopied ? '#8bc34a' : '#5755df', color: '#fff', borderColor: shareCopied ? '#8bc34a' : '#5755df', minWidth: 86, textAlign: 'center' }}
                    onClick={() => { navigator.clipboard.writeText(shareUrl); setShareCopied(true); setTimeout(() => setShareCopied(false), 1500); }}
                  >{shareCopied ? '복사 완료' : '링크 복사'}</button>
                  <button
                    style={{ ...s.shareActionBtn, background: '#FEE500', border: 'none', padding: '0 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    onClick={() => {
                      if (!window.Kakao) return;
                      if (!window.Kakao.isInitialized()) window.Kakao.init('1c487e4eaa7b1b677c3f3f5f2bb70163');
                      const token = new URL(shareUrl).searchParams.get('token');
                      window.Kakao.Share.sendCustom({
                        templateId: 134953,
                        templateArgs: { TOKEN: token, THU: 'https://jerry2126.vommedia.co.kr/vommedia2.jpg', TITLE: projectName || '', PW: sharePw || '없음' },
                      });
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 4C11.163 4 4 9.82 4 17c0 4.754 3.07 8.926 7.698 11.345L10.1 34.77a.5.5 0 0 0 .718.543L18.2 30.8c.595.06 1.196.1 1.8.1 8.837 0 16-5.82 16-13S28.837 4 20 4z" fill="#3C1E1E"/>
                    </svg>
                  </button>
                </div>
              </>)}
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={s.delBox} onClick={e => e.stopPropagation()}>
            <div style={s.delTitle}>영상 삭제</div>
            <div style={s.delDesc}><strong style={{ color: '#fff' }}>{deleteConfirm.name}</strong>을(를) 휴지통으로 이동합니다.</div>
            <div style={s.delActions}>
              <button style={s.delBtn} onClick={() => setDeleteConfirm(null)}>취소</button>
              <button style={{ ...s.delBtn, ...s.delBtnDanger }} onClick={() => handleDelete(deleteConfirm.id)}>이동</button>
            </div>
          </div>
        </div>
      )}

      {/* 선택 삭제 모달 */}
      {deleteSelectedConfirm && (
        <div style={s.overlay} onClick={() => setDeleteSelectedConfirm(false)}>
          <div style={s.delBox} onClick={e => e.stopPropagation()}>
            <div style={s.delTitle}>선택 삭제</div>
            <div style={s.delDesc}>선택한 {selectedCount}개 영상을 휴지통으로 이동합니다.<br/>이 작업은 되돌릴 수 있습니다.</div>
            <div style={s.delActions}>
              <button style={s.delBtn} onClick={() => setDeleteSelectedConfirm(false)}>취소</button>
              <button style={{ ...s.delBtn, ...s.delBtnDanger }} onClick={handleDeleteSelected}>이동</button>
            </div>
          </div>
        </div>
      )}

      {/* 업로드 슬라이드 패널 */}
      {uploadOpen && (
        <div style={{ ...s.panelOverlay, pointerEvents: panelVisible ? 'all' : 'none' }}
          onClick={closeUpload}>
          <div style={{ ...s.uploadPanel, transform: panelVisible ? 'translateX(0)' : 'translateX(100%)' }}
            onClick={e => e.stopPropagation()}>
            <div style={s.uploadHeader}>
              <span style={s.uploadTitle}>영상 추가</span>
              <button style={s.uploadClose} onClick={closeUpload}>
                <svg width="27" height="27" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div style={s.uploadBody}>
              <div style={s.dropZone}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
                  <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                </svg>
                <span style={s.dropLabel}>파일을 여기에 드래그하거나<br/>클릭하여 선택하세요</span>
                <span style={s.dropSub}>MP4, MOV, AVI 등 · 여러 파일 동시 선택 가능</span>
              </div>
              <input ref={fileInputRef} type="file" accept="video/*" multiple style={{ display: 'none' }}
                onChange={e => addFiles(e.target.files)} />

              {uploadFiles.length > 0 && (
                <div style={s.fileList}>
                  {uploadFiles.map((item, i) => (
                    <div key={i} style={s.fileRow}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.6" strokeLinecap="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                      </svg>
                      <div style={s.fileInfo}>
                        <div style={s.fileName}>{item.file.name}</div>
                        <div style={s.fileSize}>{(item.file.size / 1024 / 1024).toFixed(1)} MB</div>
                        <div style={s.progressBarBg}>
                          <div style={{ ...s.progressBarFill, width: item.progress + '%', background: item.status === 'done' ? '#28c76f' : item.status === 'error' ? '#e74c3c' : '#5755df' }} />
                        </div>
                      </div>
                      {!uploading && item.status !== 'done' && (
                        <button style={s.fileRemove}
                          onClick={() => setUploadFiles(prev => prev.filter((_, j) => j !== i))}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div style={s.uploadActions}>
                <button style={s.uploadBtn} onClick={closeUpload} disabled={uploading}>취소</button>
                {allDone
                  ? <button style={{ ...s.uploadBtn, ...s.uploadBtnPrimary }} onClick={closeUpload}>완료</button>
                  : <button style={{ ...s.uploadBtn, ...s.uploadBtnPrimary }} onClick={handleUpload} disabled={uploading || uploadFiles.length === 0}>업로드</button>
                }
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap: { minHeight: '100vh', background: '#191919', fontFamily: "'Pretendard', sans-serif", padding: 20, boxSizing: 'border-box' },
  topbar: { borderTop: '1px solid rgba(255,255,255,0.2)', borderBottom: '1px solid rgba(255,255,255,0.2)', padding: '0 18px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 19 },
  topbarLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  divider: { width: 1, height: 18, background: 'rgba(255,255,255,0.15)', flexShrink: 0 },
  topbarPath: { display: 'flex', alignItems: 'center', gap: 16 },
  topbarDim: { fontSize: '13pt', fontWeight: 500, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.5px', cursor: 'pointer' },
  topbarCurrent: { fontSize: '13pt', fontWeight: 400, color: '#ffffff', letterSpacing: '0.5px' },
  shareBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', fontSize: '11pt', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 },
  deleteSelectedBtn: { background: 'none', border: '0.56px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', fontSize: '11pt', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  controlRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 17 },
  sectionLabel: { fontSize: 12, color: 'rgba(255,255,255,0.4)' },
  sliderGroup: { display: 'flex', alignItems: 'center', gap: 10, height: 24 },
  sliderBtn: { color: 'rgba(255,255,255,0.5)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center' },
  slider: { width: 90, height: 2, cursor: 'pointer' },
  grid: { display: 'flex', flexWrap: 'wrap', gap: 38, justifyContent: 'flex-start' },
  listWrap: { display: 'flex', flexDirection: 'column', gap: 8 },
  listRow: { display: 'flex', alignItems: 'center', gap: 14, padding: '8px 14px', border: '0.56px solid rgba(255,255,255,0.2)', borderRadius: 10, cursor: 'pointer', minHeight: 48, boxSizing: 'border-box' },
  listThumb: { width: 72, aspectRatio: '16/9', background: '#0e0e0e', borderRadius: 5, overflow: 'hidden', flexShrink: 0 },
  listName: { display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 },
  videoCard: { background: 'transparent', border: '0.56px solid rgba(255,255,255,0.2)', borderRadius: 16, overflow: 'hidden', cursor: 'pointer', display: 'flex', flexDirection: 'column' },
  thumbnail: { width: '100%', aspectRatio: '16/9', background: '#0e0e0e', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover' },
  thumbPlaceholder: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 },
  playIcon: { width: 40, height: 40, borderRadius: '50%', border: '1.5px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.2)' },
  thumbLabel: { fontSize: 10, color: 'rgba(255,255,255,0.15)', letterSpacing: '0.5px' },
  durationBadge: { position: 'absolute', bottom: 7, right: 8, fontSize: 10, color: 'rgba(255,255,255,0.5)', background: 'rgba(0,0,0,0.5)', padding: '2px 6px', borderRadius: 4 },
  deleteBtn: { position: 'absolute', top: 8, right: 8, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', border: 'none', borderRadius: 6, cursor: 'pointer', color: 'rgba(255,255,255,0.4)', padding: 0 },
  cardBody: { padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', gap: 5 },
  cardName: { display: 'flex', alignItems: 'center', gap: 6 },
  cardNameSpan: { fontSize: '11pt', fontWeight: 400, color: '#fff', flex: 1 },
  pencilBtn: { width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', padding: 0, flexShrink: 0 },
  nameInput: { flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid #5755df', borderRadius: 6, padding: '2px 7px', fontSize: '11pt', color: '#fff', outline: 'none', fontFamily: 'inherit' },
  cardMeta: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  cardInfo: { fontSize: 11, color: 'rgba(255,255,255,0.3)' },
  badgeNew: { fontSize: 10, padding: '2px 8px', borderRadius: 20, background: 'rgba(87,85,223,0.2)', color: '#a5a3ff', border: '1px solid rgba(87,85,223,0.3)' },
  statusPill: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.35)' },
  statusDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  addCard: { borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 },
  overlay: { position: 'fixed', inset: 0, zIndex: 100 },
  delBox: { background: '#303030', border: '0.56px solid rgba(255,255,255,0.1)', borderRadius: 14, padding: '28px 24px', width: 320, boxSizing: 'border-box', position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 201 },
  delTitle: { fontSize: '13pt', fontWeight: 600, color: '#fff', marginBottom: 10 },
  delDesc: { fontSize: '10pt', color: 'rgba(255,255,255,0.45)', lineHeight: 1.6, marginBottom: 22 },
  delActions: { display: 'flex', justifyContent: 'flex-end', gap: 8 },
  delBtn: { fontSize: '10pt', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', border: '0.56px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.5)', fontFamily: 'inherit' },
  delBtnDanger: { background: '#e74c3c', color: '#fff', borderColor: '#e74c3c' },
  panelOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0)', zIndex: 100 },
  uploadPanel: { position: 'fixed', top: 20, right: 0, bottom: 0, width: 380, background: '#1e1e1f', borderLeft: '0.56px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', transition: 'transform 0.25s ease', zIndex: 101 },
  uploadHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', height: 56, borderTop: '1px solid rgba(255,255,255,0.2)', borderBottom: '1px solid rgba(255,255,255,0.2)', flexShrink: 0 },
  uploadTitle: { fontSize: '12pt', color: '#fff', fontWeight: 500 },
  uploadClose: { background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center' },
  uploadBody: { padding: 16, paddingTop: 35, display: 'flex', flexDirection: 'column', gap: 14, flex: 1, overflowY: 'auto' },
  dropZone: { border: '1.5px dashed rgba(255,255,255,0.2)', borderRadius: 12, padding: '28px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, cursor: 'pointer', color: 'rgba(255,255,255,0.2)', flexShrink: 0 },
  dropLabel: { fontSize: '11pt', color: 'rgba(255,255,255,0.4)', textAlign: 'center', lineHeight: 1.6 },
  dropSub: { fontSize: '9pt', color: 'rgba(255,255,255,0.2)' },
  fileList: { display: 'flex', flexDirection: 'column', gap: 6 },
  fileRow: { background: 'rgba(255,255,255,0.04)', border: '0.56px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '9px 12px', display: 'flex', alignItems: 'center', gap: 10 },
  fileInfo: { flex: 1, minWidth: 0 },
  fileName: { fontSize: '10pt', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileSize: { fontSize: '9pt', color: 'rgba(255,255,255,0.3)', marginTop: 2 },
  progressBarBg: { width: '100%', height: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2, marginTop: 5 },
  progressBarFill: { height: '100%', borderRadius: 2, transition: 'width .3s' },
  fileRemove: { background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center' },
  shareField: { display: 'flex', flexDirection: 'column', gap: 6 },
  shareLabel: { display: 'block', fontSize: '10pt', color: 'rgba(255,255,255,0.4)', marginBottom: 6 },
  shareInput: { width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '9px 12px', fontSize: '11pt', color: '#fff', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' },
  shareActions: { display: 'flex', justifyContent: 'flex-end', gap: 8 },
  shareActionBtn: { fontSize: '10pt', padding: '7px 16px', borderRadius: 8, cursor: 'pointer', border: '0.56px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.5)', fontFamily: 'inherit' },
  shareUrlBox: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 12px', fontSize: '10pt', color: 'rgba(255,255,255,0.6)', wordBreak: 'break-all', lineHeight: 1.5 },
  modalLabel: { fontSize: '10pt', color: 'rgba(255,255,255,0.4)', marginBottom: 6 },
  modalInput: { width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 12px', fontSize: '11pt', color: '#fff', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' },
  uploadActions: { display: 'flex', justifyContent: 'flex-end', gap: 8 },
  uploadBtn: { fontSize: '10pt', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', border: '0.56px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.5)', fontFamily: 'inherit' },
  uploadBtnPrimary: { background: '#5755df', color: '#fff', borderColor: '#5755df' },
};
